import React, { useCallback, useEffect, useMemo, useState } from "react";

import { format } from "date-fns";

import { useAppSelector } from "app/hooks";
import { CustomFilterOption } from "components/custom-filters-list/custom-filters-list.component";
import {
  selectCurrentClinic,
  selectCurrentPractice,
  selectCurrentRegionClinicsIds,
  selectCurrentUser,
  selectUserClinicsIds
} from "features/auth/auth-slice";
import usePaginationV2 from "hooks/use-pagination-v2.hook";
import usePersistedState from "hooks/use-persisted-state";
import { FAXES_QUERY } from "services/faxes";
import {
  FaxFilters,
  FaxResponse,
  FaxesQueryParams
} from "services/types/fax.type";
import { determineClinicFiltering } from "utils/clinics.util";

import useFaxesCustomFilterOptions, {
  FaxesCustomFiltersValues
} from "./use-faxes-custom-filter-options.hook";

type DateRange = {
  after_date: string | null;
  before_date: string | null;
};

const FaxesListContext = React.createContext<
  | ({
      faxes: FaxResponse[];
      fetchMore: () => void;
      isLoading: boolean;
      currentFilters: FaxesQueryParams;
      setCurrentFilters: React.Dispatch<React.SetStateAction<FaxesQueryParams>>;
      updateCustomFilter: (field: keyof FaxesQueryParams, value: any) => void;
      customFilters: CustomFilterOption[];
      selectedCustomFilters: FaxesCustomFiltersValues;
      refetch: () => void;
      updateDateRange: React.Dispatch<React.SetStateAction<DateRange>>;
      includeOtherClinics: boolean;
      setIncludeOtherClinics: (value: boolean) => void;
      updatePaginatedData: (id: number) => void;
      isDataPending: boolean;
    } & DateRange)
  | null
>(null);

type FaxesProviderProps = {
  children?: React.ReactNode;
};

export function FaxesListProvider({ children }: FaxesProviderProps) {
  const [includeOtherClinics, setIncludeOtherClinics] =
    usePersistedState<boolean>("useFaxes/includeOtherClinics", false);
  const currentPractice = useAppSelector(selectCurrentPractice);
  const currentClinic = useAppSelector(selectCurrentClinic);
  const currentRegionClinicsIds = useAppSelector(selectCurrentRegionClinicsIds);
  const userClinicsIds = useAppSelector(selectUserClinicsIds);
  const currentUser = useAppSelector(selectCurrentUser);

  const [{ after_date, before_date }, updateDateRange] = useState<DateRange>({
    before_date: null,
    after_date: null
  });

  const [selectedCustomFilters, setSelectedCustomFilters] =
    useState<FaxesCustomFiltersValues>({});

  const faxesFilters: FaxesQueryParams = useMemo(() => {
    const clinic = !currentPractice?.limit_to_current_clinic
      ? determineClinicFiltering({
          currentClinic,
          includeOtherClinics,
          currentRegionClinicsIds,
          isShowOwnClinicsOnlyEnabled: currentUser?.show_own_clinics_only,
          userClinicsIds
        })
      : currentClinic?.id;

    return {
      query: FAXES_QUERY,
      clinic
    };
  }, [
    currentClinic,
    currentPractice?.limit_to_current_clinic,
    currentRegionClinicsIds,
    userClinicsIds,
    currentUser?.show_own_clinics_only,
    includeOtherClinics
  ]);

  const [currentFilters, setCurrentFilters] =
    useState<FaxesQueryParams>(faxesFilters);

  const updateCustomFilter = useCallback(
    (field: keyof FaxesQueryParams, value: any) => {
      return setCurrentFilters((prevFilters) => ({
        ...prevFilters,
        [field]: value
      }));
    },
    [setCurrentFilters]
  );

  const customFilters = useFaxesCustomFilterOptions({
    updateCustomFilter,
    selectedCustomFilters,
    setSelectedCustomFilters
  });

  useEffect(() => {
    const formatDate = (dateStr: string | null) => {
      try {
        return dateStr ? format(new Date(dateStr), "yyyy-MM-dd") : undefined;
      } catch (e) {
        console.error(`Error parsing date: ${dateStr}, ${e}`);
        return undefined;
      }
    };
    setCurrentFilters((prev) => ({
      ...prev,
      after_date: formatDate(after_date),
      before_date: formatDate(before_date)
    }));
  }, [after_date, before_date]);

  useEffect(() => {
    setCurrentFilters((prevFilters) => ({
      ...prevFilters,
      ...faxesFilters
    }));
  }, [faxesFilters]);

  const {
    data: faxes = [],
    isLoading,
    fetchMore,
    refetch,
    updatePaginatedData,
    isDataPending
  } = usePaginationV2<FaxFilters, FaxResponse>({
    endpointName: "getFaxesPaginated",
    limit: 25,
    params: currentFilters
  });

  return (
    <FaxesListContext.Provider
      value={{
        faxes,
        fetchMore,
        isLoading,
        currentFilters,
        setCurrentFilters,
        customFilters,
        updateCustomFilter,
        selectedCustomFilters,
        refetch,
        after_date,
        before_date,
        updateDateRange,
        includeOtherClinics,
        setIncludeOtherClinics,
        updatePaginatedData,
        isDataPending
      }}
    >
      {children}
    </FaxesListContext.Provider>
  );
}

export default function useFaxesList() {
  const context = React.useContext(FaxesListContext);
  if (!context) {
    throw new Error(`useFaxesList must be used within a FaxesListProvider`);
  }

  return context;
}
